import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { useSidebar } from '@/routes/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { StartPage } from '@/routes/_embaUserLayout/-components/StartPage';
import { QuestionPage } from '../-Page';

import { useLeaveBlocker } from '@/routes/_embaUserLayout/-hooks/useLeaveBlocker';
import { useAnswerSkillCheckTestMutation } from '../-hooks/useAnswerSkillCheckTestMutation';
import { useStartSkillCheckTestMutation } from '../-hooks/useStartSkillCheckTestMutation';

export const COURSE_SKILL_CHECK_TEST = graphql(/* GraphQL */ `
  fragment EmbaCourseSkillCheckTest on CourseSkillCheckTest {
    id
    questionsCount
    timeLimitMinutes
    embaCourse{
      id
      name
    }
    unsolvedQuestions{
      id
      ...EmbaCourseSkillCheckTestQuestionListItem
    }
  }
`);

export type SkillCheckTestContainerProps = {
  embaCourseSkillCheckTest: FragmentType<typeof COURSE_SKILL_CHECK_TEST>;
  userAttendanceSlug: string;
  retry?: boolean;
};

export const SkillCheckTestContainer = ({
  embaCourseSkillCheckTest,
  userAttendanceSlug,
  retry,
}: SkillCheckTestContainerProps) => {
  const { initBlocking, unBlocking } = useLeaveBlocker();

  const skillCheckTestData = useFragment(
    COURSE_SKILL_CHECK_TEST,
    embaCourseSkillCheckTest,
  );

  const { embaCourse, unsolvedQuestions } = skillCheckTestData;

  const [
    embaUserCourseSkillCheckTestResultId,
    setEmbaUserCourseSkillCheckTestResultId,
  ] = useState<number | null>(null);

  const { startSkillCheckTest, startSkillCheckTestMutationError } =
    useStartSkillCheckTestMutation(
      userAttendanceSlug,
      setEmbaUserCourseSkillCheckTestResultId,
    );

  if (startSkillCheckTestMutationError) {
    throw startSkillCheckTestMutationError;
  }

  const {
    answerSkillCheckTest,
    answerSkillCheckTestMutationError,
    answerSkillCheckTestMutationLoading,
  } = useAnswerSkillCheckTestMutation(
    userAttendanceSlug,
    embaUserCourseSkillCheckTestResultId,
  );

  if (answerSkillCheckTestMutationError) {
    throw answerSkillCheckTestMutationError;
  }

  const { hideSidebar } = useSidebar();
  const [answers, setAnswers] = useState<{
    [key: string]: { questionId: string; choiceIds: string[] };
  }>({});
  const allQuestionsAnswered = unsolvedQuestions.every(
    (question) => answers[question.id] !== undefined,
  );
  const [remainingSeconds, setRemainingSeconds] = useState(
    skillCheckTestData.timeLimitMinutes * 60,
  );
  const [endUnixTime, setEndUnixTime] = useState<number>(0);
  const [processState, setProcessState] = useState<'start' | 'in progress'>(
    'start',
  );

  // React.StrictModeで再実行されるのを防ぐ
  const hasRetried = useRef(false);

  // 2重送信防止のためのフラグ
  const isSubmitted = useRef(false);

  const startTest = useCallback(() => {
    startSkillCheckTest();
    setProcessState('in progress');
    hideSidebar();
    setEndUnixTime(
      Date.now() + skillCheckTestData.timeLimitMinutes * 60 * 1000,
    );
  }, [startSkillCheckTest, hideSidebar, skillCheckTestData.timeLimitMinutes]);

  const handleSingleChoiceChange = (questionId: number, choiceId: number) => {
    setAnswers({
      ...answers,
      [questionId]: {
        questionId: questionId.toString(),
        choiceIds: [choiceId.toString()],
      },
    });
  };

  const handleMultipleChoiceChange = (questionId: number, choiceId: number) => {
    const currentChoices = answers[questionId]?.choiceIds || [];

    if (currentChoices.includes(choiceId.toString())) {
      // チェックを外した時
      setAnswers({
        ...answers,
        [questionId]: {
          questionId: questionId.toString(),
          choiceIds: currentChoices.filter((id) => id !== choiceId.toString()),
        },
      });
    } else {
      setAnswers({
        ...answers,
        [questionId]: {
          questionId: questionId.toString(),
          choiceIds: [...currentChoices, choiceId.toString()],
        },
      });
    }
  };

  const handleSubmit = useCallback(
    async (timeUp = false) => {
      unBlocking();
      await answerSkillCheckTest(Object.values(answers), timeUp);
    },
    [answers, answerSkillCheckTest, unBlocking],
  );

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // 自動回答送信タイマー
  useEffect(() => {
    if (processState === 'in progress') {
      const interval = setInterval(() => {
        // テスト開始時に設定した終了時刻と、現在時刻の差分を計算
        const currentUnixTime = Date.now();
        const diff = Math.max(0, endUnixTime - currentUnixTime);
        setRemainingSeconds(Math.ceil(diff / 1000));

        // 時間切れで自動送信
        if (diff <= 0 && !isSubmitted.current) {
          isSubmitted.current = true;
          clearInterval(interval);
          handleSubmit(true);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }

    return undefined;
  }, [processState, endUnixTime, handleSubmit]);

  // リトライ時の処理
  useEffect(() => {
    if (!hasRetried.current && retry && processState === 'start') {
      hasRetried.current = true;
      startTest();
    }
  }, [retry, processState, startTest]);

  if (processState === 'start') {
    return (
      <StartPage
        onClick={startTest}
        chapterName={embaCourse.name}
        lessonName="スキルチェックテスト"
        lessonType="skill_check_test"
        questionsCount={skillCheckTestData.questionsCount}
        timeLimitMinutes={skillCheckTestData.timeLimitMinutes}
      />
    );
  }

  return (
    <QuestionPage
      embaCourseName={embaCourse.name}
      questions={unsolvedQuestions}
      allQuestionsAnswered={allQuestionsAnswered}
      remainingTimeText={formatTime(remainingSeconds)}
      handleSingleChoiceChange={handleSingleChoiceChange}
      handleMultipleChoiceChange={handleMultipleChoiceChange}
      handleSubmit={handleSubmit}
      initBlocking={initBlocking}
      answerSkillCheckTestMutationLoading={answerSkillCheckTestMutationLoading}
    />
  );
};
